import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Chip,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel,
  Grid,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import UserManagementDialog from './UserManagementDialog';
import { useOrgCuratedCurrencies } from '../hooks/useOrgCuratedCurrencies';
import { formatCurrencyPickerLabel } from '../utils/iso4217';
import {
  CompanyDashboardProvider,
  useCompanyDashboard
} from './companyDashboard/CompanyDashboardContext';
import CompanyDashboardControls from './companyDashboard/CompanyDashboardControls';
import CompanyDashboardSection from './companyDashboard/CompanyDashboardSection';
import CompanyAccountsTable from './companyDashboard/CompanyAccountsTable';
import CompanyBudgetRollupCard from './companyDashboard/CompanyBudgetRollupCard';

interface Company {
  id: string;
  name: string;
  companyNumber?: string | null;
  organizationId: string;
  description?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  companyType: 'external' | 'internal' | 'system';
  currency: string;
  isActive: boolean;
  defaultTasks?: string[];
  createdAt: string;
  updatedAt: string;
}

interface CompanyFormData {
  name: string;
  companyNumber: string;
  description: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  companyType: 'external' | 'internal' | 'system';
  currency: string;
  isActive: boolean;
  defaultTasks: string[];
}

interface Account {
  id: string;
  name: string;
  companyId: string;
  organizationId: string;
  description?: string;
  isActive: boolean;
}

interface CompanyUser {
  id: string;
  email: string;
  displayName?: string;
  role: string;
  isActive: boolean;
}

interface CompanyViewProps {
  companyId: string;
  companyName: string;
  organizationId: string;
  onBack: () => void;
  onOpenAccount: (accountId: string, accountName: string) => void;
  userRole?: string;
}

export default function CompanyView(props: CompanyViewProps) {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCompany(null);
    setLoading(true);
    api
      .getCompany(props.companyId)
      .then((response) => {
        if (response.success && response.data) {
          setCompany(response.data);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [props.companyId]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading company...</Typography>
      </Box>
    );
  }

  if (!company) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Company not found</Alert>
        <Button onClick={props.onBack} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <CompanyDashboardProvider companyId={props.companyId} companyCurrency={company.currency}>
      <CompanyViewBody {...props} initialCompany={company} />
    </CompanyDashboardProvider>
  );
}

function CompanyViewBody({
  companyId,
  organizationId,
  onBack,
  onOpenAccount,
  userRole = 'MEMBER',
  initialCompany
}: CompanyViewProps & { initialCompany: Company }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { accessGranted } = useCompanyDashboard();
  const [error, setError] = useState<string | null>(null);
  const [company, setCompany] = useState<Company | null>(initialCompany);
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [organizationDefaultTasks, setOrganizationDefaultTasks] = useState<string[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [formData, setFormData] = useState<CompanyFormData>({
    name: initialCompany.name,
    companyNumber: initialCompany.companyNumber || '',
    description: initialCompany.description || '',
    contactEmail: initialCompany.contactEmail || '',
    contactPhone: initialCompany.contactPhone || '',
    address: initialCompany.address || '',
    companyType: initialCompany.companyType,
    currency: initialCompany.currency || 'EUR',
    isActive: initialCompany.isActive,
    defaultTasks: initialCompany.defaultTasks || []
  });
  const [newTaskInput, setNewTaskInput] = useState('');
  const { curatedCodes } = useOrgCuratedCurrencies(organizationId);

  const canEdit = userRole === 'OWNER' || userRole === 'MANAGER';
  const economyVisible = accessGranted === true;

  const handleDeleteCompany = async () => {
    if (!window.confirm('Are you sure you want to delete this company? This cannot be undone.')) {
      return;
    }
    try {
      await api.deleteCompany(companyId);
      onBack();
    } catch (err: any) {
      console.error('Error deleting company:', err);
      setError(err.response?.data?.message || 'Failed to delete company');
    }
  };

  const loadCompany = async () => {
    try {
      setError(null);
      const response = await api.getCompany(companyId);
      if (response.success && response.data) {
        const companyData = response.data;
        setCompany(companyData);
        setFormData({
          name: companyData.name,
          companyNumber: companyData.companyNumber || '',
          description: companyData.description || '',
          contactEmail: companyData.contactEmail || '',
          contactPhone: companyData.contactPhone || '',
          address: companyData.address || '',
          companyType: companyData.companyType,
          currency: companyData.currency || 'EUR',
          isActive: companyData.isActive,
          defaultTasks: companyData.defaultTasks || []
        });
      } else {
        setError('Company not found');
      }
    } catch (err: any) {
      console.error('Error loading company:', err);
      setError(err.response?.data?.message || 'Failed to load company');
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await api.getCompanyUsers(companyId);
      const transformedUsers =
        response.data?.map((user: any) => ({
          id: user.id,
          email: user.email,
          displayName: user.displayName,
          role: user.role,
          isActive: user.isActive || true
        })) || [];
      setUsers(transformedUsers);
    } catch (err: any) {
      console.error('Error loading users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      setAccountsLoading(true);
      const response = await api.getAccounts({ companyId, isActive: 'true' });
      setAccounts(response.data || []);
    } catch (err: any) {
      console.error('Error loading accounts:', err);
    } finally {
      setAccountsLoading(false);
    }
  };

  const loadOrganizationDefaultTasks = async () => {
    try {
      const response = await api.getUserOrganizations();
      if (response.success && response.data) {
        const userOrg = response.data.find((uo: any) => uo.organization.id === organizationId);
        if (userOrg) {
          setOrganizationDefaultTasks(userOrg.organization.defaultTasks || []);
        }
      }
    } catch (err: any) {
      console.error('Error loading organization default tasks:', err);
    }
  };

  useEffect(() => {
    loadCompany();
    loadUsers();
    loadAccounts();
    loadOrganizationDefaultTasks();
  }, [companyId, organizationId]);

  useEffect(() => {
    const state = location.state as any;
    if (state?.context?.type === 'company' && state?.context?.id === companyId) {
      setAccounts([]);
      loadAccounts();
      window.history.replaceState({}, document.title);
    }
  }, [location.state, companyId]);

  const saveCompanyUpdate = async (payload: {
    organizationId: string;
    name: string;
    companyNumber?: string;
    description?: string;
    contactEmail?: string;
    contactPhone?: string;
    address?: string;
    companyType: string;
    currency: string;
    isActive: boolean;
    defaultTasks: string[];
  }) => {
    const response = await api.updateCompany(companyId, payload);
    if (!response.success) throw new Error(response.message || 'Failed to update company');
    return response;
  };

  const handleSave = async () => {
    try {
      setError(null);
      await saveCompanyUpdate({ ...formData, organizationId });
      await loadCompany();
      setEditDialogOpen(false);
    } catch (err: any) {
      console.error('Error updating company:', err);
      setError(err.response?.data?.message || 'Failed to update company');
    }
  };

  const handleAddDefaultTask = async (taskName: string) => {
    if (!taskName || formData.defaultTasks.includes(taskName)) return;
    const newTasks = [...formData.defaultTasks, taskName];
    setFormData((prev) => ({ ...prev, defaultTasks: newTasks }));
    try {
      setError(null);
      await saveCompanyUpdate({ ...formData, defaultTasks: newTasks, organizationId });
      setCompany((prev) => (prev ? { ...prev, defaultTasks: newTasks } : null));
    } catch (err: any) {
      console.error('Error saving default task:', err);
      setError(err.response?.data?.message || 'Failed to save default task');
      loadCompany();
    }
  };

  const handleRemoveDefaultTask = async (taskName: string) => {
    const newTasks = formData.defaultTasks.filter((t) => t !== taskName);
    setFormData((prev) => ({ ...prev, defaultTasks: newTasks }));
    try {
      setError(null);
      await saveCompanyUpdate({ ...formData, defaultTasks: newTasks, organizationId });
      setCompany((prev) => (prev ? { ...prev, defaultTasks: newTasks } : null));
    } catch (err: any) {
      console.error('Error saving default task:', err);
      setError(err.response?.data?.message || 'Failed to save default task');
      loadCompany();
    }
  };

  const getCompanyTypeColor = (type: string) => {
    switch (type) {
      case 'external':
        return 'primary';
      case 'internal':
        return 'secondary';
      case 'system':
        return 'default';
      default:
        return 'default';
    }
  };

  if (!company) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{company.name}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditDialogOpen(true)}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteCompany}
              >
                Delete
              </Button>
            </>
          )}
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
        </Box>
      </Box>

      <CompanyDashboardControls />
      <CompanyDashboardSection />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <CompanyAccountsTable
                  accounts={accounts}
                  accountsLoading={accountsLoading}
                  canEdit={canEdit}
                  onOpenAccount={onOpenAccount}
                  onAddAccount={() =>
                    navigate(`/org/${organizationId}/manage/add-account?companyId=${companyId}`)
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2
                  }}
                >
                  <Typography variant="h6">Users ({users.length})</Typography>
                  {canEdit && (
                    <Button
                      size="small"
                      startIcon={<PeopleIcon />}
                      onClick={() => setUsersDialogOpen(true)}
                    >
                      Manage
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                {usersLoading ? (
                  <LinearProgress />
                ) : users.length > 0 ? (
                  <List dense>
                    {users.slice(0, 10).map((user) => (
                      <ListItem key={user.id}>
                        <ListItemText
                          primary={user.displayName || user.email}
                          secondary={user.email}
                        />
                        <ListItemSecondaryAction>
                          <Chip label={user.role} size="small" />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                    {users.length > 10 && (
                      <ListItem>
                        <ListItemText
                          primary={`... and ${users.length - 10} more`}
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        />
                      </ListItem>
                    )}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No users assigned to this company.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Company Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1">{company.name}</Typography>
                  </Box>
                  {company.companyNumber && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Company number
                      </Typography>
                      <Typography variant="body1">{company.companyNumber}</Typography>
                    </Box>
                  )}
                  {company.description && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body1">{company.description}</Typography>
                    </Box>
                  )}
                  {company.contactEmail && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Contact Email
                      </Typography>
                      <Typography variant="body1">
                        <a href={`mailto:${company.contactEmail}`}>{company.contactEmail}</a>
                      </Typography>
                    </Box>
                  )}
                  {company.contactPhone && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Contact Phone
                      </Typography>
                      <Typography variant="body1">{company.contactPhone}</Typography>
                    </Box>
                  )}
                  {company.address && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body1">{company.address}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Currency
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrencyPickerLabel(company.currency || 'EUR')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Type
                    </Typography>
                    <Chip
                      label={company.companyType}
                      color={getCompanyTypeColor(company.companyType) as any}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={company.isActive ? 'Active' : 'Inactive'}
                      color={company.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {economyVisible && <CompanyBudgetRollupCard companyId={companyId} />}
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Company</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Company number"
              value={formData.companyNumber}
              onChange={(e) => setFormData({ ...formData, companyNumber: e.target.value })}
              fullWidth
              helperText="Optional reference code (not unique)"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Contact Email"
              value={formData.contactEmail}
              onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
              fullWidth
              type="email"
            />
            <TextField
              label="Contact Phone"
              value={formData.contactPhone}
              onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
              fullWidth
            />
            <TextField
              label="Address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Company Type</InputLabel>
              <Select
                value={formData.companyType}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    companyType: e.target.value as CompanyFormData['companyType']
                  })
                }
                label="Company Type"
              >
                <MenuItem value="external">External</MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
                <MenuItem value="system">System</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                label="Currency"
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                disabled={curatedCodes.length === 0}
              >
                {curatedCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {formatCurrencyPickerLabel(code)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />

            <Divider />
            <Typography variant="subtitle1">Default Tasks</Typography>
            {organizationDefaultTasks.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Inherited from Organization:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {organizationDefaultTasks.map((task) => (
                    <Chip key={task} label={task} size="small" color="primary" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}
            <TextField
              label="Add Company-Specific Task"
              placeholder="Enter task name and press Enter"
              size="small"
              value={newTaskInput}
              onChange={(e) => setNewTaskInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newTaskInput.trim()) {
                  handleAddDefaultTask(newTaskInput.trim());
                  setNewTaskInput('');
                }
              }}
              fullWidth
            />
            {formData.defaultTasks.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.defaultTasks.map((task) => (
                  <Chip
                    key={task}
                    label={task}
                    onDelete={() => handleRemoveDefaultTask(task)}
                    size="small"
                  />
                ))}
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {canEdit && (
        <UserManagementDialog
          open={usersDialogOpen}
          onClose={() => {
            setUsersDialogOpen(false);
            loadUsers();
          }}
          title={`Manage Users - ${company.name}`}
          entityId={companyId}
          entityType="company"
          organizationId={organizationId}
        />
      )}
    </Box>
  );
}
